import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { NotebookPen, Plus, Pin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

const COLORS = {
  amber: "bg-amber-50 border-amber-200",
  rose: "bg-rose-50 border-rose-200",
  teal: "bg-teal-50 border-teal-200",
  indigo: "bg-indigo-50 border-indigo-200",
  slate: "bg-slate-50 border-slate-200",
};

export default function NotesPage() {
  const { user, customRole } = useAuth();
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", color: "amber" });
  const [editing, setEditing] = useState(null);

  const load = async () => setNotes((await api.get("/notes")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title && !form.body) return;
    await api.post("/notes", form);
    setForm({ title: "", body: "", color: "amber" });
    toast.success("Note saved");
    load();
  };

  const togglePin = async (n) => { await api.patch(`/notes/${n.note_id}`, { pinned: !n.pinned }); load(); };
  const remove = async (n) => { if (!window.confirm("Delete this note?")) return; await api.delete(`/notes/${n.note_id}`); load(); };
  const saveEdit = async () => {
    if (!editing) return;
    await api.patch(`/notes/${editing.note_id}`, { title: editing.title, body: editing.body, color: editing.color });
    setEditing(null);
    load();
  };

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="notes-page">
        <PageHeader title="Notes" subtitle="Capture ideas, briefs, and reminders." icon={NotebookPen} accent="amber" />

        <form onSubmit={add} className="dashboard-card p-5 mb-5">
          <div className="space-y-3">
            <input className="input-pill" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notes-title-input" />
            <textarea
              rows={5}
              className="input-pill resize-y min-h-[110px] font-mono text-sm leading-relaxed"
              placeholder="Note body — press Enter for new line, paste multi-line text, tabs preserved…"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") add(e); }}
              data-testid="notes-body-input"
            />
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {Object.keys(COLORS).map((c) => (
              <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-slate-900" : "border-slate-200"} ${COLORS[c]}`} data-testid={`notes-color-${c}`} />
            ))}
            <span className="text-[10px] text-slate-400 ml-1 hidden sm:inline">Tip: <kbd className="px-1 py-0.5 bg-slate-100 rounded font-mono">⌘/Ctrl + Enter</kbd> to save</span>
            <button type="submit" className="btn-primary ml-auto py-2 px-4 text-sm" data-testid="notes-save"><Plus className="w-3 h-3" /> Add note</button>
          </div>
        </form>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {notes.length === 0 && <div className="text-sm text-slate-400 col-span-full text-center py-8">No notes yet.</div>}
          {notes.map((n) => (
            <div key={n.note_id} className={`p-4 rounded-xl border ${COLORS[n.color] || COLORS.amber} relative group`} data-testid={`note-${n.note_id}`}>
              <div className="flex items-start justify-between mb-1">
                <button onClick={() => setEditing(n)} className="font-semibold text-slate-800 text-left flex-1 hover:underline truncate">{n.title || "Untitled"}</button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => togglePin(n)} className={`p-1 rounded ${n.pinned ? "text-cyan-600" : "text-slate-400"}`}><Pin className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(n)} className="p-1 rounded text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap break-words font-mono leading-relaxed max-h-72 overflow-y-auto pr-1">{n.body}</div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 relative">
            <ModalCloseBtn onClose={() => setEditing(null)} />
              <h3 className="font-display font-black text-xl mb-3">Edit note</h3>
              <input className="input-pill mb-2" placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <textarea
                rows={10}
                className="input-pill mb-3 resize-y min-h-[200px] font-mono text-sm leading-relaxed"
                placeholder="Body — Enter for new line, paste multi-line text, tabs preserved"
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") saveEdit(); }}
              />
              <div className="flex items-center gap-2 mb-3">
                {Object.keys(COLORS).map((c) => (
                  <button type="button" key={c} onClick={() => setEditing({ ...editing, color: c })} className={`w-7 h-7 rounded-full border-2 ${editing.color === c ? "border-slate-900" : "border-slate-200"} ${COLORS[c]}`} />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="btn-ghost flex-1 py-2">Cancel</button>
                <button onClick={saveEdit} className="btn-primary flex-1 py-2">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
